import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  Link,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TimerIcon from '@mui/icons-material/Timer';
import RepeatIcon from '@mui/icons-material/Repeat';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  ShiftType,
  SHIFT_LABELS,
  detectShift,
  getLocalDateString,
  ZNUNY_INSTANCES,
  Customer,
  CUSTOMER_COLORS,
} from '../types/shift';
import { listShiftSummaries, getShiftSummaryByDateAndShift, ShiftSummaryRecord } from '../services/shiftApi';
import { lastNShiftMetrics, sparklineSummary, customerDelta, ShiftMetrics } from '../utils/trends';
import { deriveMaintenance, upcomingWithinDays } from '../utils/maintenance';

const TREND_WINDOW = 14;

const znunyUrlFor = (customer: string, ticketNumber: string): string | null => {
  const instance = ZNUNY_INSTANCES[customer as Customer];
  if (!instance || !ticketNumber) return null;
  return `https://${instance}/otrs/index.pl?Action=AgentTicketZoom;TicketNumber=${ticketNumber}`;
};

interface TrendTileProps {
  title: string;
  value: string | number;
  color: string;
  icon: React.ReactNode;
  values: number[];
  unit?: string;
  tooltip?: string;
  onClick: () => void;
  below?: React.ReactNode;
}

const TrendTile: React.FC<TrendTileProps> = ({ title, value, color, icon, values, unit, tooltip, onClick, below }) => {
  const summary = sparklineSummary(values);
  const ariaLabel = values.length > 0
    ? `${title} over last ${values.length} shifts: min ${summary.min}${unit ?? ''}, max ${summary.max}${unit ?? ''}, current ${summary.current}${unit ?? ''}. Open drill-down.`
    : `${title}: ${value}${unit ?? ''}. Open drill-down.`;

  return (
    <Paper
      sx={{
        borderTop: `4px solid ${color}`,
        overflow: 'hidden',
        bgcolor: '#0f172a',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 20px rgba(0,0,0,0.4)` },
      }}
    >
      <Tooltip title={tooltip ?? ''} arrow disableHoverListener={!tooltip}>
        <Box
          component="button"
          onClick={onClick}
          aria-label={ariaLabel}
          sx={{
            width: '100%',
            p: 2.5,
            bgcolor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            color: 'inherit',
            font: 'inherit',
            display: 'block',
            '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: -2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ color, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {icon}
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
              </Typography>
            </Box>
          </Box>
          <Typography variant="h3" fontWeight={700} sx={{ color, lineHeight: 1.1 }}>
            {value}{unit ?? ''}
          </Typography>
          {values.length > 1 ? (
            <Box sx={{ mt: 1, height: 40 }}>
              <SparkLineChart
                data={values}
                height={40}
                curve="monotoneX"
                colors={[color]}
                showHighlight
                showTooltip
              />
            </Box>
          ) : (
            <Typography sx={{ mt: 1, fontSize: '0.7rem', color: '#475569', fontStyle: 'italic' }}>
              Need at least 2 shifts for a trend.
            </Typography>
          )}
        </Box>
      </Tooltip>
      {below}
    </Paper>
  );
};

const ShiftDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [today, setToday] = useState(() => getLocalDateString());
  const [currentShift, setCurrentShift] = useState<ShiftType>(() => detectShift());
  const [summaries, setSummaries] = useState<ShiftSummaryRecord[]>([]);
  const [currentShiftSummary, setCurrentShiftSummary] = useState<ShiftSummaryRecord | null>(null);
  const [carryOverOpen, setCarryOverOpen] = useState(false);
  const [pdOnCall, setPdOnCall] = useState<string>('');

  const fetchSummaries = useCallback(async () => {
    try {
      const [data, current] = await Promise.all([
        listShiftSummaries(100),
        getShiftSummaryByDateAndShift(today, currentShift),
      ]);
      setSummaries(data);
      setCurrentShiftSummary(current);
    } catch (err) {
      console.error('Failed to load shift summaries:', err);
    }
  }, [today, currentShift]);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  // Re-evaluate current date/shift across focus/visibility/interval.
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const nextDate = getLocalDateString(now);
      const nextShift = detectShift(now);
      if (nextDate !== today) setToday(nextDate);
      if (nextShift !== currentShift) setCurrentShift(nextShift);
      if (nextDate === today && nextShift === currentShift) fetchSummaries();
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(check, 60_000);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [today, currentShift, fetchSummaries]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('https://zo421qtcy7.execute-api.us-east-1.amazonaws.com/oncall');
        const data = await resp.json();
        const oncalls: { name: string }[] = data.oncalls || [];
        setPdOnCall([...new Set(oncalls.map((o) => o.name))].join(', '));
      } catch { /* ignore */ }
    })();
  }, []);

  // ── Derived trend + snapshot data ──
  const trend = useMemo<ShiftMetrics[]>(() => lastNShiftMetrics(summaries, TREND_WINDOW), [summaries]);
  const latest = useMemo<ShiftMetrics | undefined>(() => {
    const rec = currentShiftSummary ?? summaries[0];
    return rec ? lastNShiftMetrics([rec], 1)[0] : undefined;
  }, [currentShiftSummary, summaries]);
  const previous = trend.length >= 2 ? trend[trend.length - 2] : undefined;

  const openSeries = trend.map((s) => s.open);
  const urgentSeries = trend.map((s) => s.urgent);
  const staleSeries = trend.map((s) => s.stale);
  const carryOverSeries = trend.map((s) => s.carryOverRate);

  const staleTickets = useMemo(() => {
    if (!latest) return [];
    const asOf = new Date();
    return latest.currentTickets
      .filter((t) => {
        if (!t.firstAppearedDate) return false;
        const first = new Date(t.firstAppearedDate);
        if (Number.isNaN(first.getTime())) return false;
        return (asOf.getTime() - first.getTime()) / 86_400_000 >= 7;
      })
      .map((t) => ({
        ticketNumber: t.ticketNumber,
        customer: t.customer,
        subject: t.ticketSubject,
        daysOnShift: Math.floor(
          (Date.now() - new Date(t.firstAppearedDate).getTime()) / 86_400_000,
        ),
        znuny: znunyUrlFor(t.customer, t.ticketNumber),
      }))
      .sort((a, b) => b.daysOnShift - a.daysOnShift);
  }, [latest]);

  const customerBreakdown = useMemo(() => {
    if (!latest) return [] as { customer: string; count: number }[];
    const counts: Record<string, number> = {};
    for (const t of latest.currentTickets) {
      const c = t.customer || 'Unknown';
      counts[c] = (counts[c] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([customer, count]) => ({ customer, count }))
      .sort((a, b) => b.count - a.count);
  }, [latest]);

  const deltas = useMemo(() => customerDelta(latest, previous), [latest, previous]);
  const maxCustomerCount = Math.max(1, ...customerBreakdown.map((c) => c.count));

  const upcomingMaintenance = useMemo(() => {
    return upcomingWithinDays(deriveMaintenance(summaries), TREND_WINDOW);
  }, [summaries]);

  // Carry-over grid: tickets (by number) that appeared anywhere in the trend window,
  // showing which shifts they were present in.
  const carryOverGrid = useMemo(() => {
    const ticketMeta: Record<string, { customer: string; subject: string }> = {};
    const presence: Record<string, Set<string>> = {}; // ticket# -> set of shiftIds
    for (const m of trend) {
      for (const t of m.currentTickets) {
        if (!t.ticketNumber) continue;
        ticketMeta[t.ticketNumber] = { customer: t.customer, subject: t.ticketSubject };
        if (!presence[t.ticketNumber]) presence[t.ticketNumber] = new Set();
        presence[t.ticketNumber].add(m.shiftId);
      }
    }
    const rows = Object.entries(ticketMeta)
      .map(([ticket, meta]) => ({
        ticket,
        customer: meta.customer,
        subject: meta.subject,
        shiftCount: presence[ticket].size,
        present: trend.map((s) => presence[ticket].has(s.shiftId)),
      }))
      .filter((r) => r.shiftCount >= 2)
      .sort((a, b) => b.shiftCount - a.shiftCount);
    return rows;
  }, [trend]);

  const lastSubmissionLabel = latest
    ? `${latest.onDutyTech} — ${latest.date} ${latest.shift}`
    : 'No shift data yet';

  return (
    <Box>
      {/* Slim Context Strip */}
      <Paper
        sx={{
          p: 2.5,
          mb: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d4f5c 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          border: '1px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: '0.1em' }}>
            NOC Dashboard · {SHIFT_LABELS[currentShift]}
          </Typography>
          {pdOnCall && (
            <Typography variant="body2" sx={{ color: '#4dd0e1', mt: 0.25 }}>
              On-Call: {pdOnCall}
            </Typography>
          )}
        </Box>
        {latest ? (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
              Last submission
            </Typography>
            <Link
              component={RouterLink}
              to={`/history/${latest.shiftId}`}
              sx={{
                color: 'white',
                fontWeight: 600,
                textDecoration: 'none',
                borderBottom: '1px dashed rgba(255,255,255,0.3)',
                '&:hover': { color: '#4dd0e1', borderBottomColor: '#4dd0e1' },
                '&:focus-visible': { outline: '2px solid #4dd0e1', outlineOffset: 2, borderRadius: '4px' },
              }}
            >
              {lastSubmissionLabel}
            </Link>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No shift data yet
          </Typography>
        )}
      </Paper>

      {/* Trend Tiles */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TrendTile
            title="Open Tickets"
            value={latest?.open ?? 0}
            color="#00bcd4"
            icon={<ConfirmationNumberIcon fontSize="small" />}
            values={openSeries}
            onClick={() => navigate('/')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TrendTile
            title="Urgent"
            value={latest?.urgent ?? 0}
            color={(latest?.urgent ?? 0) > 0 ? '#ef4444' : '#10b981'}
            icon={<WarningAmberIcon fontSize="small" />}
            values={urgentSeries}
            onClick={() => navigate('/?filter=urgent')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TrendTile
            title="Stale (7+ days)"
            value={latest ? staleTickets.length : 0}
            color={staleTickets.length > 0 ? '#f59e0b' : '#10b981'}
            icon={<TimerIcon fontSize="small" />}
            values={staleSeries}
            onClick={() => navigate('/?filter=stale')}
            below={
              staleTickets.length > 0 && (
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', px: 2, py: 1, bgcolor: 'rgba(15,23,42,0.5)' }}>
                  {staleTickets.slice(0, 5).map((t) => (
                    <Box key={t.ticketNumber} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, fontSize: '0.75rem' }}>
                      <Chip label={t.customer} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: CUSTOMER_COLORS[t.customer] || '#64748b', color: 'white' }} />
                      {t.znuny ? (
                        <Link href={t.znuny} target="_blank" rel="noopener noreferrer" sx={{ color: '#4dd0e1', display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.75rem' }}>
                          #{t.ticketNumber}<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
                        </Link>
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#e2e8f0' }}>#{t.ticketNumber}</Typography>
                      )}
                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.subject}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 600 }}>{t.daysOnShift}d</Typography>
                    </Box>
                  ))}
                  {staleTickets.length > 5 && (
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic', pt: 0.5 }}>
                      +{staleTickets.length - 5} more — click tile to see all
                    </Typography>
                  )}
                </Box>
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TrendTile
            title="Carry-Over Rate"
            value={latest?.carryOverRate ?? 0}
            unit="%"
            color="#a78bfa"
            icon={<RepeatIcon fontSize="small" />}
            values={carryOverSeries}
            tooltip="Share of this shift's tickets that carried over from a prior shift."
            onClick={() => setCarryOverOpen(true)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Tickets by Customer */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tickets by Customer
            </Typography>
            {customerBreakdown.length === 0 ? (
              <Typography color="text.secondary">No ticket data yet.</Typography>
            ) : (
              customerBreakdown.map(({ customer, count }) => {
                const delta = deltas[customer];
                return (
                  <Box
                    key={customer}
                    component={RouterLink}
                    to={`/?filter=customer&customer=${encodeURIComponent(customer)}`}
                    aria-label={`${count} ${customer} tickets this shift. Open customer group in Handoff Form.`}
                    sx={{
                      display: 'block',
                      mb: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: '6px',
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: 'rgba(0, 188, 212, 0.06)' },
                      '&:focus-visible': { outline: '2px solid #00bcd4', outlineOffset: 2 },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {customer}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {count}
                        </Typography>
                        {delta !== null && delta !== undefined && (
                          <Chip
                            label={delta === 0 ? '—' : delta > 0 ? `+${delta}` : `${delta}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: delta === 0 ? 'rgba(148,163,184,0.2)' : delta > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
                              color: delta === 0 ? '#94a3b8' : delta > 0 ? '#fca5a5' : '#6ee7b7',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count / maxCustomerCount) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#334155',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: CUSTOMER_COLORS[customer] || '#00bcd4',
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>

        {/* Upcoming Maintenance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon fontSize="small" sx={{ color: '#f59e0b' }} />
              Upcoming Maintenance ({TREND_WINDOW} days)
            </Typography>
            {upcomingMaintenance.length === 0 ? (
              <Typography color="text.secondary">Nothing scheduled.</Typography>
            ) : (
              upcomingMaintenance.slice(0, 8).map((m) => {
                const monthParam = m.scheduledDate.slice(0, 7);
                return (
                  <Box key={`${m.ticketNumber}-${m.scheduledDate}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <Chip label={m.customer} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: CUSTOMER_COLORS[m.customer] || '#64748b', color: 'white' }} />
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#e2e8f0' }}>
                      #{m.ticketNumber}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.description}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 600 }}>
                      {m.scheduledDate}
                    </Typography>
                    <Tooltip title="Open in Handoff Form" arrow>
                      <Link
                        component={RouterLink}
                        to={`/?filter=maintenance&ticket=${encodeURIComponent(m.ticketNumber)}`}
                        aria-label={`Open ticket ${m.ticketNumber} in Handoff Form`}
                        sx={{ color: '#64748b', display: 'inline-flex', p: 0.5, borderRadius: '4px', '&:hover': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' }, '&:focus-visible': { outline: '2px solid #00bcd4', outlineOffset: 2 } }}
                      >
                        <AssignmentIcon sx={{ fontSize: '1rem' }} />
                      </Link>
                    </Tooltip>
                    <Tooltip title="View on Calendar" arrow>
                      <Link
                        component={RouterLink}
                        to={`/calendar?month=${monthParam}&ticket=${encodeURIComponent(m.ticketNumber)}`}
                        aria-label={`View ticket ${m.ticketNumber} on Calendar for ${monthParam}`}
                        sx={{ color: '#64748b', display: 'inline-flex', p: 0.5, borderRadius: '4px', '&:hover': { color: '#f59e0b', bgcolor: 'rgba(245,158,11,0.08)' }, '&:focus-visible': { outline: '2px solid #f59e0b', outlineOffset: 2 } }}
                      >
                        <EventIcon sx={{ fontSize: '1rem' }} />
                      </Link>
                    </Tooltip>
                  </Box>
                );
              })
            )}
            {upcomingMaintenance.length > 8 && (
              <Link
                component={RouterLink}
                to="/calendar"
                sx={{ display: 'block', textAlign: 'center', mt: 1.5, fontSize: '0.8rem', color: '#4dd0e1' }}
              >
                +{upcomingMaintenance.length - 8} more — see Calendar →
              </Link>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Carry-Over Detail Dialog */}
      <Dialog
        open={carryOverOpen}
        onClose={() => setCarryOverOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: '12px', border: '1px solid rgba(167,139,250,0.3)' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          Carry-Over Detail — last {trend.length} shifts
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {carryOverGrid.length === 0 ? (
            <Typography color="#94a3b8" sx={{ py: 2 }}>
              No ticket carried across two or more shifts in this window.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontWeight: 600 }}>Ticket</TableCell>
                    <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontWeight: 600 }}>Customer</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontWeight: 600 }}>Shifts</TableCell>
                    {trend.map((s) => (
                      <TableCell key={s.shiftId} align="center" sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontSize: '0.65rem', px: 0.5 }}>
                        <Box>{s.date.slice(5)}</Box>
                        <Box sx={{ fontWeight: 400, fontSize: '0.6rem' }}>{s.shift.slice(0, 3)}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carryOverGrid.map((row) => {
                    const znuny = znunyUrlFor(row.customer, row.ticket);
                    return (
                      <TableRow key={row.ticket}>
                        <TableCell>
                          {znuny ? (
                            <Link href={znuny} target="_blank" rel="noopener noreferrer" sx={{ color: '#4dd0e1', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              #{row.ticket}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>#{row.ticket}</Typography>
                          )}
                          <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{row.subject}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={row.customer} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: CUSTOMER_COLORS[row.customer] || '#64748b', color: 'white' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#a78bfa' }}>{row.shiftCount}</Typography>
                        </TableCell>
                        {row.present.map((on, i) => (
                          <TableCell key={i} align="center" sx={{ p: 0.5 }}>
                            <Box sx={{
                              width: 14,
                              height: 14,
                              mx: 'auto',
                              borderRadius: '3px',
                              bgcolor: on ? (CUSTOMER_COLORS[row.customer] || '#a78bfa') : 'rgba(255,255,255,0.04)',
                              border: on ? 'none' : '1px solid rgba(255,255,255,0.06)',
                            }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
          <Button onClick={() => setCarryOverOpen(false)} sx={{ color: '#94a3b8' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftDashboard;
